import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  $transferRequestsUploadFileStore,
  $transferRequestsDeleteFileStore,
  $transferUpdateStatusStore,
  $transferRequestsLimitsStore,
  $transferRequestsCreateStore,
  $transferRequestsUpdateStore,
  $transferReasonsStore,
  $transferRequestsGetFxRateStore,
} from '@lib/store';
import { AmountUtils, cn, DateUtils, PaletteUtils, PaymentUtils } from '@shared/utils';
import type { AccountResponse, RecipientResponse, TransferRequestResponse } from '@shared/api';
import { Avatar } from './avatar';
import { RecentRecipient } from '../select';
import { ExchangeRate } from '../exchangeRate';
import { Button, FileUpload, Icon, Text, Modal, Spinner, FileWithId, CustomFile } from '@shared/widgets';
import WarningIcon from '@assets/icons/warning.svg';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountResponse;
  accountTo?: AccountResponse | null;
  recipient?: RecipientResponse | null;
  files: (FileWithId | CustomFile)[];
  onUploadFile?: (files: (FileWithId | CustomFile)[]) => void;
  onDeleteFile?: (files: (FileWithId | CustomFile)[]) => void;
  onConfirm: (vopForceUpdate?: boolean) => Promise<void>;
}

export const PaymentModal = (props: PaymentModalProps) => {
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const fxRateStore = useUnit($transferRequestsGetFxRateStore.store);
  const uploadFileStore = useUnit($transferRequestsUploadFileStore.store);
  const deleteFileStore = useUnit($transferRequestsDeleteFileStore.store);
  const transferRequestLimit = useUnit($transferRequestsLimitsStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const updateStatusStore = useUnit($transferUpdateStatusStore.store);
  const transferData = (transferRequestsUpdateStore.data || transferRequestsCreateStore.data) as TransferRequestResponse;

  const { t } = useTranslation('payments');

  useEffect(() => {
    $transferRequestsLimitsStore.events.getLimits();
    return () => $transferRequestsLimitsStore.events.clear();
  }, []);

  const uploadFileHandler = async (files: (FileWithId | CustomFile)[]) => {
    const response = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferData.id, files });
    props.onUploadFile?.(response);
  };
  const deleteFileHandler = async (file: FileWithId | CustomFile) => {
    const files = await PaymentUtils.deletePaymentDocument({ transferRequestId: transferData.id, files: props.files, file });
    props.onDeleteFile?.(files);
  };

  const purpose = useMemo(
    () => transferReasonsStore.data.find((reason) => reason.id === transferData.purpose)?.name || '',
    [transferReasonsStore.data, transferData.purpose],
  );
  const content = useMemo(
    () => [
      { label: t('modals.payment.fields.senderCurrency'), value: transferData.senderCurrency },
      { label: t('modals.payment.fields.beneficiaryCurrency'), value: transferData.beneficiaryCurrency },
      // {
      //   label: t('modals.payment.fields.senderAmount'),
      //   value: AmountUtils.transformToAmountWithCurrency(transferData.senderAmount, transferData.senderCurrency),
      // },
      {
        label: t('modals.payment.fields.beneficiaryAmount'),
        value: AmountUtils.transformToAmountWithCurrency(transferData.beneficiaryAmount, transferData.beneficiaryCurrency),
      },
      // {
      //   label: t('modals.payment.fields.totalAmount'),
      //   value: AmountUtils.transformToAmountWithCurrency(transferData.totalAmount, transferData.senderCurrency),
      // },
      { label: t('modals.payment.fields.purpose'), value: purpose },
      {
        hidden: !(props.recipient ? props.account.currency !== props.recipient.currency : props.account.currency !== props.accountTo?.currency),
        label: t('modals.payment.fields.exchangeRate'),
        value: (
          <span className='flex items-center gap-2 text-sm font-semibold text-soft-black dark:text-white'>
            {AmountUtils.transformToAmountWithCurrency(1, props.account?.currency)} ={' '}
            {AmountUtils.transformToAmountWithCurrency(fxRateStore.data, props.recipient ? props.recipient.currency : props.accountTo?.currency)}
            <ExchangeRate />
          </span>
        ),
      },
      // { label: t('modals.payment.fields.fastPayment'), value: props.isFastPayment ? t('boolean.yes', { ns: 'common' }) : t('boolean.no', { ns: 'common' }) },
      { label: t('modals.payment.fields.transactionReference'), value: transferData.reasonOfThePayment },
    ],
    [transferData, purpose, props.recipient, props.account, props.accountTo, fxRateStore.data, t],
  );
  const isLimitReached = transferData.senderAmount >= transferRequestLimit.data.outgoingTransferLimit;
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={
        <>
          <div className='flex items-center justify-between'>
            <Text className={cn(PaletteUtils.isInsigniaPalette && 'font-[Prata]')} size='3xl'>
              {props?.account?.currency &&
                AmountUtils.transformToAmountWithCurrency(transferData.beneficiaryAmount, transferData.beneficiaryCurrency)}
            </Text>
          </div>
          {/* <Text size='sm' color='secondary'>
            {t('modals.payment.title')} {AmountUtils.transformToAmountWithCurrency(transferData?.fee || 0)} {props?.account?.currency}
          </Text> */}
          <Text className={cn('mb-5', PaletteUtils.isVcfcPalette && 'mb-0')} size='sm' color='secondary'>
            {DateUtils.formatDate(dayjs().format(), { withTime: true })}
          </Text>
        </>
      }
      width='500px'
    >
      <hr className={cn(PaletteUtils.isVcfcPalette && 'text-gray-black')} />
      <div
        className={cn(
          'mb-3 mt-4 flex p-3',
          PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-eerie-black',
          PaletteUtils.isVcfcPalette && 'bg-white dark:bg-light/5',
        )}
      >
        <Avatar />
        <div className='my-auto ml-3 flex flex-col justify-between'>
          {props.account.name ? (
            <>
              <Text weight='semibold'>{props.account.name}</Text>
              {props.account.accountNumber && <Text size='xs'>{props.account.accountNumber}</Text>}
            </>
          ) : (
            <Text weight='semibold'>{props.account.accountNumber || '-'}</Text>
          )}
        </div>
      </div>
      {props.recipient && (
        <div
          className={cn(
            'my-auto flex items-center p-3',
            PaletteUtils.isVcfcPalette && 'bg-white dark:bg-light/5',
            PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-eerie-black',
          )}
        >
          <RecentRecipient recipient={props.recipient} />
        </div>
      )}
      {props.accountTo && (
        <div
          className={cn(
            'mb-3 mt-4 flex p-3',
            PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-eerie-black',
            PaletteUtils.isVcfcPalette && 'bg-white dark:bg-light/5',
          )}
        >
          <Avatar />
          <div className='my-auto ml-3 flex flex-col justify-between'>
            {props.accountTo.name ? (
              <>
                <Text weight='semibold'>{props.accountTo.name}</Text>
                {props.accountTo.accountNumber && <Text size='xs'>{props.accountTo.accountNumber}</Text>}
              </>
            ) : (
              <Text weight='semibold'>{props.accountTo.accountNumber || '-'}</Text>
            )}
          </div>
        </div>
      )}
      <ul className='mt-3 flex flex-col gap-2'>
        {content.map((item, idx) => {
          if (item.hidden) return null;
          return (
            <li key={idx}>
              <Text size='sm' color='secondary'>
                {item.label}
              </Text>
              <Text weight='semibold' size='sm'>
                {item.value}
              </Text>
            </li>
          );
        })}
      </ul>
      {(isLimitReached || props.files) && (
        <>
          {isLimitReached && (
            <div className={cn('mt-4 w-full rounded-sm border-none bg-ice p-2 pb-0.5 dark:bg-slate', PaletteUtils.isVcfcPalette && 'p-0')}>
              <Text size='sm' color='secondary'>
                {t('modals.payment.documents.title')}
              </Text>
              <div className={cn('flex flex-col gap-2', PaletteUtils.isVcfcPalette && 'bg-white p-3 dark:bg-soft-black')}>
                <div className='flex items-start justify-start'>
                  <div className='mr-3 rounded-sm bg-light-carmine-pink p-3'>
                    <Icon icon={<WarningIcon />} color='red' />
                  </div>
                  <div className='flex flex-col items-start justify-start'>
                    <Text>{t('modals.payment.documents.description')}</Text>
                    <ul className='mt-2 pl-4'>
                      <li className='list-disc'>{t('modals.payment.documents.fields.agreements')}</li>
                      <li className='list-disc'>{t('modals.payment.documents.fields.invoices')}</li>
                      <li className='list-disc'>{t('modals.payment.documents.fields.receipts')}</li>
                    </ul>
                  </div>
                </div>
                {PaletteUtils.isVcfcPalette && <Text>{t('modals.payment.documents.helpText')}</Text>}
              </div>
            </div>
          )}
          {(uploadFileStore.loading || deleteFileStore.loading) && <Spinner position />}
          {!uploadFileStore.loading && !deleteFileStore.loading && (
            <FileUpload
              className='mt-4'
              multiple
              value={props.files}
              onChange={(files) => uploadFileHandler(files)}
              onDelete={(file) => deleteFileHandler(file)}
            />
          )}
        </>
      )}
      <div className={cn('mt-6 flex gap-3', PaletteUtils.isVcfcPalette && 'mt-3')}>
        <Button color='outlined' fullWidth onClick={props.onClose}>
          {t('modals.payment.buttons.return')}
        </Button>
        <Button color='primary' fullWidth disabled={updateStatusStore.loading} onClick={() => props.onConfirm()}>
          {t('buttons.send', { ns: 'common' })}
        </Button>
      </div>
    </Modal>
  );
};
